import type { HTMLAttributes, ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** `raised` sits higher on the canvas; `sunken` recedes into it. */
  tone?: 'default' | 'raised' | 'sunken';
  /** Apply a translucent, blurred surface. Use sparingly for floating panels. */
  glass?: boolean;
  padded?: boolean;
  children: ReactNode;
}

/**
 * A layered content surface. Kept solid by default so text contrast is never
 * compromised; opt into `glass` only for floating or navigation surfaces.
 */
export function Card({
  tone = 'default',
  glass = false,
  padded = true,
  className = '',
  children,
  ...rest
}: CardProps) {
  const toneClass =
    tone === 'raised'
      ? 'bg-surface-raised'
      : tone === 'sunken'
        ? 'bg-surface-sunken'
        : 'bg-surface';

  return (
    <div
      className={[
        'rounded-lg border border-line shadow-card',
        glass ? 'glass' : toneClass,
        padded ? 'p-5 sm:p-6' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </div>
  );
}

interface SectionHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Small label above the title, e.g. a step or category. */
  eyebrow?: ReactNode;
  /** Action rendered to the right, aligned to the baseline. */
  action?: ReactNode;
  id?: string;
  className?: string;
}

/** Standard heading block used at the top of every card/panel. */
export function SectionHeader({
  title,
  description,
  eyebrow,
  action,
  id,
  className = '',
}: SectionHeaderProps) {
  return (
    <div
      className={['flex flex-wrap items-start justify-between gap-3', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="min-w-0">
        {eyebrow ? (
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-accent-strong">
            {eyebrow}
          </p>
        ) : null}
        <h2 id={id} className="text-lg font-semibold tracking-tight text-ink">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 max-w-prose text-sm text-ink-soft">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
